import { useEffect, useMemo, useRef, useState } from 'react';
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { ApiEvent } from '@/api';
import {
  addMinutes,
  parseLocal,
  toLocalInputValue,
  ensureSeconds,
  proximityBucket,
  energyLevel,
  formatLocaleShort
} from '@/utils/datetime';
import { DEFAULT_DURATION_MIN, downloadICSForEvent, eventStartISO } from '@/utils/event';
import { CountdownHero } from '@/components/CountdownHero';
import { FiArchive, FiCalendar, FiTrash2, FiClock, FiMapPin } from 'react-icons/fi';

function ensureEnergyCSS() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('energy-css')) return;
  const css = `
  @media (prefers-reduced-motion: no-preference) {
    @keyframes energyShimmer {
      0%   { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  }
  .energy-shimmer {
    position: absolute; inset: 0; border-radius: inherit; pointer-events: none;
    mix-blend-mode: screen;
    background:
      linear-gradient(120deg,
        rgba(255,255,255,0) 0%,
        rgba(255,255,255,0.06) 35%,
        rgba(255,255,255,0.22) 50%,
        rgba(255,255,255,0.06) 65%,
        rgba(255,255,255,0) 100%);
    background-size: 200% 100%;
    animation: energyShimmer var(--shimmerSpeed, 6s) linear infinite;
    opacity: 0;
  }
  .energy-tint {
    position: absolute; inset: -2px; border-radius: inherit; pointer-events: none;
    background:
      radial-gradient(120% 120% at 20% 0%,
        color-mix(in oklab, var(--accent-color), transparent 92%), transparent 60%),
      radial-gradient(140% 110% at 100% 100%,
        color-mix(in oklab, var(--accent-color), transparent 94%), transparent 60%);
    opacity: 0;
    transition: opacity 200ms ease;
  }
  `;
  const el = document.createElement('style');
  el.id = 'energy-css';
  el.textContent = css;
  document.head.appendChild(el);
}

export function EditableCard({
  ev, manual, inArchivedView, onSave, onDelete, onArchive, dragHandleProps, now
}: {
  ev: ApiEvent;
  manual: boolean;
  inArchivedView: boolean;
  onSave: (patch: { title?: string; start_ts?: string; end_ts?: string; location?: string; }) => Promise<void>;
  onDelete: () => Promise<void>;
  onArchive: (archived: boolean) => Promise<void>;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  now: Date;
}) {
  useEffect(() => { ensureEnergyCSS(); }, []);

  // local editable state
  const [title, setTitle] = useState(ev.title);
  const [dt, setDt] = useState(toLocalInputValue(parseLocal(eventStartISO(ev))));
  const [loc, setLoc] = useState(ev.location || '');

  // inline edit flags (so it looks like the locked card until clicked)
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingLoc, setEditingLoc] = useState(false);
  const [editingDt, setEditingDt] = useState(false);

  const titleRef = useRef<HTMLInputElement | null>(null);
  const locRef = useRef<HTMLInputElement | null>(null);
  const dtRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { if (editingTitle) titleRef.current?.focus(); }, [editingTitle]);
  useEffect(() => { if (editingLoc)    locRef.current?.focus();    }, [editingLoc]);
  useEffect(() => { if (editingDt)     dtRef.current?.focus();     }, [editingDt]);

  async function commit() {
    const okTitle = !!title.trim() && title.trim().length >= 2;
    const okDt = !!dt && !Number.isNaN(new Date(dt).getTime());
    const okLoc = !!loc.trim() && loc.trim().length >= 2;
    if (!okTitle || !okDt || !okLoc) return;

    const start = parseLocal(dt);
    const end = addMinutes(start, DEFAULT_DURATION_MIN); // still used for ICS convenience only
    await onSave({
      title: title.trim(),
      start_ts: toLocalInputValue(start) + ':00',
      end_ts: toLocalInputValue(end) + ':00',
      location: loc.trim()
    });
  }

  function onKeyEditable(
    e: React.KeyboardEvent<HTMLInputElement>,
    stopEditing: () => void
  ) {
    if (e.key === 'Enter')  { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
    if (e.key === 'Escape') { stopEditing(); (e.target as HTMLInputElement).blur(); }
  }

  // reflect unsaved edits in countdown + proximity/energy (NO end)
  const startIso = ensureSeconds(dt);
  const prox = proximityBucket(startIso, now);
  const energy = useMemo(() => energyLevel(startIso, now), [startIso, now]);
  const friendly = formatLocaleShort(startIso);

  // once-per-(event,day) micro-flare (same behavior as LockedCard)
  const daysLeft = useMemo(() => {
    const nowMs = now.getTime();
    const startMs = parseLocal(startIso).getTime();
    return Math.max(0, Math.ceil((startMs - nowMs) / 86400000));
  }, [startIso, now]);
  const [flare, setFlare] = useState(false);
  const flareKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${ev.id}:d${daysLeft}`;
    if (flareKeyRef.current === key) return;
    flareKeyRef.current = key;
    if (typeof sessionStorage !== 'undefined' && daysLeft > 0 && !sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      setFlare(true);
      const t = setTimeout(() => setFlare(false), 950);
      return () => clearTimeout(t);
    }
  }, [daysLeft, ev.id]);

  const shimmerSpeed = (6 - 4 * energy).toFixed(2) + 's';
  const shimmerOpacity = 0.06 + 0.22 * energy;
  const tintOpacity = 0.05 + 0.25 * energy;
  const saturate = (1 + 0.45 * energy).toFixed(3);
  const brighten = (1 + 0.03 * energy).toFixed(3);

  return (
    <article
      className={`card glass card--event ${flare ? 'is-flare' : ''}`}
      role="listitem"
      aria-label={`Edit ${ev.title}`}
      data-prox={prox}
      data-energy={energy.toFixed(3)}
      style={{
        filter: `saturate(${saturate}) brightness(${brighten})`,
        ['--shimmerSpeed' as any]: shimmerSpeed,
        ['--progress' as any]: energy  // drives subtle progress effects
      }}
    >
      {flare && <div className="flare-layer" aria-hidden />}

      {energy > 0 ? (
        <>
          <div className="energy-shimmer" aria-hidden style={{ opacity: shimmerOpacity }} />
          <div className="energy-tint" aria-hidden style={{ opacity: tintOpacity }} />
        </>
      ) : null}

      {manual && (
        <div className="card-handle" title="Drag to reorder" aria-hidden="true" {...dragHandleProps}>≡</div>
      )}

      <div className="card-fab">
        <button
          className="btn icon small"
          title="Add to Calendar"
          aria-label="Add to Calendar"
          onClick={() => downloadICSForEvent(
            ev,
            title || ev.title,
            loc || ev.location || undefined
          )}
        >
          <FiCalendar />
        </button>
        <button
          className="btn icon small"
          title={inArchivedView ? 'Unarchive' : 'Archive'}
          aria-label={inArchivedView ? 'Unarchive' : 'Archive'}
          onClick={() => onArchive(!inArchivedView)}
        >
          <FiArchive />
        </button>
        <button
          className="btn icon small"
          title="Delete"
          aria-label="Delete"
          onClick={() => { if (confirm('Delete this event?')) onDelete(); }}
        >
          <FiTrash2 />
        </button>
      </div>

      {/* Title: locked look until edit */}
      <h3 className="card-title-center">
        {editingTitle ? (
          <input
            ref={titleRef}
            className="editlike name-input"
            value={title}
            onChange={(e)=>setTitle(e.target.value)}
            onBlur={() => { setEditingTitle(false); void commit(); }}
            onKeyDown={(e)=>onKeyEditable(e, () => setEditingTitle(false))}
            aria-label={`Event name for ${ev.title}`}
            style={{ textAlign: 'center' }}
          />
        ) : (
          <button
            type="button"
            className="editlike"
            aria-label="Edit title"
            onClick={()=>setEditingTitle(true)}
            style={{
              textAlign: 'center',
              fontWeight: 800,
              letterSpacing: '-0.01em',
              fontSize: 'inherit',
              padding: 0,
              border: '1px solid transparent',
              background: 'transparent',
              width: '100%'
            }}
          >
            {title || 'Untitled'}
          </button>
        )}
      </h3>

      {/* Countdown is ONLY until start; no "live" */}
      <CountdownHero startIso={startIso} now={now} randSeed={ev.id} />

      {/* Meta row — identical to locked; click to edit */}
      <div className="card-meta-row">
        <div className="meta meta-left" style={{ minWidth: 0 }}>
          <FiMapPin aria-hidden="true" />
          {editingLoc ? (
            <input
              ref={locRef}
              className="editlike loc-input"
              value={loc}
              placeholder="Location"
              onChange={(e)=>setLoc(e.target.value)}
              onBlur={() => { setEditingLoc(false); void commit(); }}
              onKeyDown={(e)=>onKeyEditable(e, () => setEditingLoc(false))}
              aria-label={`Location for ${ev.title}`}
              style={{
                minWidth: 0,
                width: '100%',
                paddingInline: 6,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden'
              }}
            />
          ) : (
            <button
              type="button"
              className="editlike"
              aria-label="Edit location"
              onClick={()=>setEditingLoc(true)}
              style={{
                minWidth: 0,
                padding: 0,
                border: '1px solid transparent',
                background: 'transparent',
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={loc || 'TBA'}
            >
              <span className="truncate">{loc || 'TBA'}</span>
            </button>
          )}
        </div>

        <div className="meta meta-right">
          <FiClock aria-hidden="true" />
          {editingDt ? (
            <input
              ref={dtRef}
              className="editlike dt-input"
              type="datetime-local"
              value={dt}
              onChange={(e)=>setDt(e.target.value)}
              onBlur={() => { setEditingDt(false); void commit(); }}
              onKeyDown={(e)=>onKeyEditable(e, () => setEditingDt(false))}
              aria-label={`Date and time for ${ev.title}`}
              style={{ textAlign: 'right' }}
            />
          ) : (
            <button
              type="button"
              className="editlike"
              aria-label="Edit date & time"
              onClick={()=>setEditingDt(true)}
              title={friendly}
              style={{
                padding: 0,
                border: '1px solid transparent',
                background: 'transparent',
                textAlign: 'right'
              }}
            >
              {friendly}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
